import React, { useContext } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormContext, useFieldArray } from "react-hook-form";

import { Button, HookSelectFormGroup, HookFormInputGroup } from "components";
import { DebounceTimeout } from "globalConstants";
import { SaveInProgressWrapperContext } from "helpers";
import {
	trimMidSentenceNormalizer,
	trimNormalizer,
	float2Normalizer,
} from "validators";
import {
	currency,
	exactNumberOfDigitsMask50,
	maxLength50,
	normalizeAll,
	onlyAlphanumericAndSpecialCharacters,
	required,
} from "validators/validation";

import styles from "../terminalSolution.module.scss";

type AccessoriesProps = {
	isLoading: boolean;
	terminalAccessoryTypes: { [key: string]: any }[];
	sectionName: string;
	isApplication?: boolean;
};

const Accessories = ({
	isLoading,
	terminalAccessoryTypes,
	sectionName,
	isApplication,
}: AccessoriesProps) => {
	const {
		register,
		control,
		watch,
		setValue,

		formState: { errors },
	} = useFormContext();

	const { handleSave } = useContext(SaveInProgressWrapperContext);

	const { fields, append, remove } = useFieldArray({
		control,
		name: sectionName,
		keyName: "ide",
	});

	const addAccessorySection = () => {
		append({
			type: "",
			otherName: "",
			price: "0.00",
			serialNumber: "",
		});
		handleSave?.();
	};

	const removeAccessorySection = index => {
		remove(index);
		handleSave?.();
	};

	return (
		<div data-autotest="accessoriesSection">
			{fields.map((accessories: { [key: string]: any }, index) => {
				const accessoriesValue = watch(`${sectionName}.${index}.type`);

				const handleChangeAccessories = option => {
					if (option.name !== "Other") {
						setValue(`${sectionName}.${index}.otherName`, "");
					}
				};
				return (
					<React.Fragment key={accessories.ide}>
						<div className="d-flex justify-content-between">
							<p className={styles.subHeader}>
								Accessories {index + 1}
							</p>
							<Button
								value="Remove Accessories"
								minWidth="110px"
								className="p-0 text-left"
								style={{
									height: " 25px",
									minHeight: "25px",
								}}
								variant="secondary"
								onClick={() => {
									removeAccessorySection(index);
								}}
							/>
						</div>
						<Row>
							<Col md={5}>
								<HookSelectFormGroup
									label="Accessories"
									options={[
										{ name: "Please Select", id: "" },
										...terminalAccessoryTypes,
									]}
									getOptionLabel={option => option.name}
									getOptionValue={option => option.id}
									placeholder="Please Select"
									name={`${sectionName}.${index}.type`}
									onChange={handleChangeAccessories}
									isSearchable={false}
									cacheOptions
									defaultOptions
									debounceTimeout={DebounceTimeout}
									blurLoading={isLoading}
									errors={errors}
									defaultValue={accessories.type}
									rules={{
										validate: {
											required,
										},
									}}
									showRequiredMark
								/>
							</Col>
							<Col md={{ span: 5, offset: 2 }}>
								{accessoriesValue?.name === "Other" && (
									<HookFormInputGroup
										name={`${sectionName}.${index}.otherName`}
										register={register}
										label="Accessory Name"
										colInput={6}
										colLabel={6}
										validator={{
											required,
										}}
										blurLoading={isLoading}
										autoComplete="off"
										showRequiredMark
										normalizeOnBlur={trimNormalizer}
										normalize={normalizeAll([
											onlyAlphanumericAndSpecialCharacters,
											trimMidSentenceNormalizer,
										])}
										errors={errors}
										defaultValue={accessories.otherName}
									/>
								)}
							</Col>
						</Row>
						<Row>
							{accessoriesValue?.id && (
								<>
									<Col md={5}>
										<HookFormInputGroup
											name={`${sectionName}.${index}.price`}
											register={register}
											label="Price Per Accessory, $"
											placeholder="0.00"
											colInput={6}
											colLabel={6}
											showRequiredMark
											validator={{ required }}
											blurLoading={isLoading}
											autoComplete="off"
											normalizeOnBlur={float2Normalizer}
											normalize={currency}
											errors={errors}
											defaultValue={accessories.price}
										/>
									</Col>

									{!isApplication && (
										<Col md={{ span: 5, offset: 2 }}>
											<HookFormInputGroup
												name={`${sectionName}.${index}.serialNumber`}
												register={register}
												label="Accessory Serial #"
												colInput={6}
												colLabel={6}
												blurLoading={isLoading}
												autoComplete="off"
												validator={{ maxLength50 }}
												normalize={normalizeAll([
													exactNumberOfDigitsMask50,
												])}
												normalizeOnBlur={trimNormalizer}
												errors={errors}
												defaultValue={
													accessories.serialNumber
												}
											/>
										</Col>
									)}
								</>
							)}
						</Row>
					</React.Fragment>
				);
			})}

			<Button
				value="Add Accessories"
				minWidth="110px"
				className="p-0 text-left mt-2"
				style={{
					height: " 25px",
					minHeight: "25px",
				}}
				variant="secondary"
				onClick={addAccessorySection}
			/>
		</div>
	);
};

export default Accessories;
